import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { dismissArtistTutorial } from 'utils/tutorialPrefs';

type Props = {
  isOpen: boolean;
  teamsHref: string;
  onClose: () => void;
};

type Step = {
  title: string;
  body: React.ReactNode;
};

const ArtistTeamTutorialModal: React.FC<Props> = ({ isOpen, teamsHref, onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const steps = useMemo<Step[]>(
    () => [
      {
        title: 'Start here: Teams',
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Your <span className="font-medium">Teams</span> area is the control center for building what customers experience and for reviewing submissions.
            </p>
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Quick link</div>
              <div className="mt-1">
                <Link
                  to={teamsHref}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Go to Teams
                </Link>
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Workflows → stages → forms',
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Workflows</span> are what your customer goes through. Each workflow is made up of <span className="font-medium">stages</span>.
            </p>
            <p className="text-sm text-gray-700">
              A stage can be anything. Right now, you can add a <span className="font-medium">Form</span> stage so customers can enter the information you request.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs font-semibold text-gray-900">Workflow</div>
                <div className="text-xs text-gray-600">Customer journey</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs font-semibold text-gray-900">Stage</div>
                <div className="text-xs text-gray-600">One step in the journey</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-xs font-semibold text-gray-900">Form</div>
                <div className="text-xs text-gray-600">Collect customer info</div>
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Review submissions with your team',
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Each stage can have a review step handled by your team from the <span className="font-medium">Teams</span> area.
            </p>
            <p className="text-sm text-gray-700">
              You can <span className="font-medium">accept</span> or <span className="font-medium">deny</span> a submission to move the customer forward (or keep them in place) in your workflow.
            </p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              Tip: Use the <span className="font-medium">Operations</span> view to handle active work quickly.
            </div>
          </div>
        ),
      },
      {
        title: 'Invite teammates & configure review',
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              In Teams you can invite members, assign responsibilities, and set your team review settings so everyone knows what to do next.
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li>Invite team members</li>
              <li>Adjust review expectations</li>
              <li>Keep approvals consistent</li>
            </ul>
          </div>
        ),
      },
      {
        title: 'Operations: see projects end-to-end',
        body: (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              From the Teams area, open <span className="font-medium">Operations</span> to view every project that’s ongoing or already completed.
            </p>
            <p className="text-sm text-gray-700">
              This is the best place to keep work moving and ensure customers are progressing through your workflows.
            </p>
          </div>
        ),
      },
    ],
    [teamsHref]
  );

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.setTimeout(() => {
      headingRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setStepIndex(0);
    setDontShowAgain(false);
  }, [isOpen]);

  const close = () => {
    if (dontShowAgain) {
      dismissArtistTutorial(365);
    }
    onClose();
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;

    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);

    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!isOpen) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-50" aria-live="polite">
      <div className="absolute inset-0 bg-gray-900/50" />

      <div className="absolute inset-0 overflow-y-auto p-4 sm:p-6">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="artist-tutorial-title"
          onKeyDown={onKeyDown}
          className="mx-auto mt-10 w-full max-w-2xl rounded-xl bg-white shadow-2xl ring-1 ring-black/5"
        >
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div>
              <h2
                id="artist-tutorial-title"
                ref={headingRef}
                tabIndex={-1}
                className="text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Welcome — quick setup guide
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                This guide will keep showing until you choose to hide it.
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close tutorial"
            >
              Close
            </button>
          </div>

          <div className="px-5 py-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-gray-900">{step.title}</div>
              <div className="text-xs text-gray-500">
                Step {stepIndex + 1} of {steps.length}
              </div>
            </div>

            <div className="mt-4">{step.body}</div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Don’t show again
              </label>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  disabled={stepIndex === 0}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>

                {!isLast ? (
                  <button
                    type="button"
                    onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>

            {!dontShowAgain ? (
              <div className="mt-3 text-xs text-gray-500">
                Leaving this unchecked means this tutorial will appear each time you return.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArtistTeamTutorialModal;
